import React, {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { ChevronDown, ChevronUp, Close } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { Button } from '../../lib/button.jsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '../../lib/command.jsx';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../../lib/input-group.jsx';
import { Input, InputHelperText, InputRoot } from '../../lib/input.jsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../lib/popover.jsx';
import { TruncateText } from '../../lib/truncate-text.jsx';
import { typographyVariants } from '../../lib/typography.jsx';
import ProgressCircular from '../Progress/ProgressCircular.jsx';
import {
  AutocompleteContext,
  useAutocompleteContext,
} from './AutocompleteContext.jsx';

type DataAttributes = {
  [K in `data-${string}`]?: string | number | boolean | undefined;
};

type ClearableProps =
  | {
      clearable?: true; // default case
      onClear: () => void;
    }
  | {
      clearable: false;
      onClear?: never;
    };

function Autocomplete<TData>({
  children,
  options,
  defaultValue = '',
  autoFocus = true,
  size = 'small',
  disabled = false,
  color = 'default',
  helperText = '',
  clearable = true,
  getOptionLabel,
  isSelectedOption,
  onClear,
  onAutocompleteOpen,
  onAutocompleteChange,
  onAutocompleteFetchOptions,
  formatAutocompleteValue,
  ...props
}: Readonly<
  {
    options: TData[]; // Should be already memoized
    defaultValue?: string;
    autoFocus?: boolean;
    size?: React.ComponentProps<typeof Input>['size'];
    disabled?: React.ComponentProps<typeof Input>['disabled'];
    color?: React.ComponentProps<typeof InputGroup>['color'];
    helperText?: string;
    getOptionLabel: (option: TData) => string;
    isSelectedOption: (option: TData) => boolean;
    onAutocompleteOpen?: () => void;
    onAutocompleteChange: (option: TData) => void;
    onAutocompleteFetchOptions?: (search: string) => Promise<void>;
    formatAutocompleteValue: (value: string) => string;
  } & ClearableProps &
    React.ComponentProps<typeof Popover>
>) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState(defaultValue);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(autocompleteValue, 500);

  const fetchUtmOptions = useEffectEvent(async () => {
    if (debouncedSearch && !isLoadingOptions) {
      try {
        setIsLoadingOptions(true);
        await onAutocompleteFetchOptions?.(debouncedSearch);
      } finally {
        setIsLoadingOptions(false);
      }
    }
  });

  const memoizedGetOptionLabel = useCallback(
    (option: TData) => getOptionLabel(option),
    [],
  );
  const memoizedIsSelectedOption = useCallback(
    (option: TData) => isSelectedOption(option),
    [],
  );
  const memoizedOnClear = useCallback(() => {
    setAutocompleteValue('');
    onClear?.();
  }, []);
  const memoizedOnAutocompleteOpen = useCallback(
    () => onAutocompleteOpen?.(),
    [],
  );
  const memoizedOnAutocompleteChange = useCallback(
    (option: TData) => onAutocompleteChange(option),
    [],
  );
  const memoizedOnAutocompleteFetchOptions = useCallback((search: string) => {
    if (onAutocompleteFetchOptions) {
      onAutocompleteFetchOptions(search);
    }
  }, []);
  const memoizedFormatAutocompleteValue = useCallback(
    (value: string) => formatAutocompleteValue(value),
    [],
  );
  const isOnAutocompleteFetchOptionsProvided = useMemo(
    () => onAutocompleteFetchOptions !== undefined,
    [onAutocompleteFetchOptions],
  );

  const contextValue = useMemo(
    () => ({
      popoverOpen,
      setPopoverOpen,
      inputRef,
      autocompleteValue,
      setAutocompleteValue,
      selectedIndex,
      setSelectedIndex,
      options,
      clearable,
      onClear: memoizedOnClear,
      onAutocompleteOpen: memoizedOnAutocompleteOpen,
      getOptionLabel: memoizedGetOptionLabel as (o: unknown) => string,
      isSelectedOption: memoizedIsSelectedOption as (o: unknown) => boolean,
      onAutocompleteChange: memoizedOnAutocompleteChange as (
        o: unknown,
      ) => void,
      formatAutocompleteValue: memoizedFormatAutocompleteValue,
      isLoadingOptions,
      color,
      size,
      disabled,
      autoFocus,
      helperText,
    }),
    [
      popoverOpen,
      setPopoverOpen,
      inputRef,
      autocompleteValue,
      setAutocompleteValue,
      selectedIndex,
      setSelectedIndex,
      options,
      memoizedOnClear,
      memoizedOnAutocompleteOpen,
      memoizedGetOptionLabel,
      memoizedIsSelectedOption,
      memoizedOnAutocompleteChange,
      memoizedFormatAutocompleteValue,
      isLoadingOptions,
      color,
      size,
      disabled,
      clearable,
      autoFocus,
      helperText,
    ],
  );

  useEffect(() => {
    if (!debouncedSearch && isOnAutocompleteFetchOptionsProvided) {
      memoizedOnAutocompleteFetchOptions(debouncedSearch);

      return;
    }

    isOnAutocompleteFetchOptionsProvided && fetchUtmOptions();
  }, [
    debouncedSearch,
    memoizedOnAutocompleteFetchOptions,
    isOnAutocompleteFetchOptionsProvided,
  ]);

  useEffect(() => {
    if (!autoFocus) return;

    setPopoverOpen(true);
    memoizedOnAutocompleteOpen();
  }, [autoFocus, memoizedOnAutocompleteOpen]);

  return (
    <AutocompleteContext.Provider value={contextValue}>
      <Popover open={popoverOpen} modal={false} {...props}>
        {children}
      </Popover>
    </AutocompleteContext.Provider>
  );
}

function AutocompleteTrigger({
  children,
  className,
  fullWidth = false,
  ...props
}: Readonly<
  React.ComponentProps<typeof PopoverTrigger> & { fullWidth?: boolean }
>) {
  const {
    color,
    size,
    clearable,
    onClear,
    popoverOpen,
    inputRef,
    autocompleteValue,
    disabled,
    helperText,
  } = useAutocompleteContext();

  const handleCloseClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      onClear?.();
    },
    [onClear],
  );

  const handleToggleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!popoverOpen) {
        inputRef.current?.select();
      }
      inputRef.current?.click();
    },
    [inputRef, popoverOpen],
  );

  return (
    <PopoverTrigger
      asChild
      className={cn(fullWidth ? 'w-full' : '', className)}
      {...props}
    >
      <InputRoot>
        <InputGroup className={'cursor-pointer'} size={size} color={color}>
          {children}

          <InputGroupAddon align="inline-end" className="pr-0">
            <Button
              icon
              data-testid="autocomplete-clear-button"
              aria-label="clear autocomplete input"
              data-dd-action-name="clear autocomplete input"
              variant="text"
              size={size}
              disabled={disabled}
              className={cn(
                'px-0',
                clearable && autocompleteValue ? 'visible' : 'invisible',
              )}
            >
              <Close onClick={handleCloseClick} />
            </Button>
          </InputGroupAddon>
          <InputGroupAddon align="inline-end">
            <Button
              icon
              data-testid="autocomplete-toggle-button"
              aria-label="toggle autocomplete popover"
              data-dd-action-name={`${popoverOpen ? 'close' : 'open'} autocomplete popover`}
              variant="text"
              size={size}
              disabled={disabled}
              onClick={handleToggleClick}
              // {...buttonProps}
            >
              {popoverOpen ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </InputGroupAddon>
        </InputGroup>
        {helperText && (
          <InputHelperText color={color} helperText={helperText} />
        )}
      </InputRoot>
    </PopoverTrigger>
  );
}

function AutocompleteStartAdornment({
  children,
  ...buttonProps
}: Readonly<React.ComponentProps<typeof Button>>) {
  const { inputRef, disabled, size } = useAutocompleteContext();

  return (
    <InputGroupAddon align="inline-start">
      <Button
        data-testid="autocomplete-start-adornment"
        tabIndex={-1}
        variant="plain"
        onClick={() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }}
        disabled={disabled}
        size={size}
        {...buttonProps}
      >
        <span
          className={typographyVariants({
            variant: 'bodyShort01',
          })}
        >
          {children}
        </span>
      </Button>
    </InputGroupAddon>
  );
}

function AutocompleteInput<TData>({
  placeholder,
  className,
  selectedOption,
  delayPopoverClosing = 200,
  onAutocompleteValueChange,
  ...props
}: Readonly<
  {
    placeholder?: string;
    className?: string;
    selectedOption: TData;
    delayPopoverClosing?: number; // delay the popover closing to allow for clicking on the popover content
    onAutocompleteValueChange: (value: string) => void;
  } & React.ComponentProps<typeof Input>
>) {
  const {
    autocompleteValue,
    setAutocompleteValue,
    formatAutocompleteValue,
    inputRef,
    setSelectedIndex,
    selectedIndex,
    setPopoverOpen,
    popoverOpen,
    onAutocompleteOpen,
    onAutocompleteChange,
    options,
    size,
    color,
    disabled,
  } = useAutocompleteContext<TData>();

  const memoizedOnAutocompleteValueChange = useCallback(
    (value: string) => onAutocompleteValueChange(value),
    [],
  );

  // Handle key navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Arrow down
      if (e.key === 'ArrowDown' && selectedIndex < options.length - 1) {
        setSelectedIndex((prev) => (prev === null ? 0 : prev + 1));
        e.preventDefault();
        return;
      }

      // Arrow up
      if (e.key === 'ArrowUp' && selectedIndex > 0) {
        setSelectedIndex((prev) =>
          prev === null ? options.length - 1 : prev - 1,
        );
        e.preventDefault();
        return;
      }

      // Enter
      if (e.key === 'Enter' && selectedIndex !== null) {
        const selectedOption = options[selectedIndex];

        if (selectedOption && !isAutocompleteOptionDisabled(selectedOption)) {
          onAutocompleteChange(selectedOption);
        }

        setSelectedIndex(0);
        setPopoverOpen(false);
        e.preventDefault();
        return;
      }

      // Escape
      if (e.key === 'Escape') {
        setSelectedIndex(0);
        setPopoverOpen(false);
        return;
      }

      // Open popover on printable key
      if (isPrintableKey(e)) {
        setPopoverOpen(true);
      }
    },
    [
      selectedIndex,
      options,
      setSelectedIndex,
      onAutocompleteChange,
      setPopoverOpen,
    ],
  );

  const handleInputClick = useCallback(() => {
    if (popoverOpen) {
      setPopoverOpen(false);
      return;
    }
    setPopoverOpen(true);
    onAutocompleteOpen();
  }, [popoverOpen, setPopoverOpen, onAutocompleteOpen]);

  // Close popover when input loses focus
  const handleBlur = useCallback(() => {
    // We delay the popover closing to allow for clicking on the popover content
    if (popoverOpen) {
      setTimeout(() => {
        setPopoverOpen(false);
      }, delayPopoverClosing);
    }
  }, [delayPopoverClosing, popoverOpen, setPopoverOpen]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAutocompleteValue(formatAutocompleteValue(e.currentTarget.value));
      memoizedOnAutocompleteValueChange(e.currentTarget.value);
    },
    [
      formatAutocompleteValue,
      memoizedOnAutocompleteValueChange,
      setAutocompleteValue,
    ],
  );

  return (
    <InputGroupInput
      size={size}
      color={color}
      disabled={disabled}
      ref={inputRef}
      data-testid="autocomplete-input"
      className={cn('truncate select-all', className)}
      placeholder={placeholder}
      value={autocompleteValue}
      onChange={handleOnChange}
      onClick={handleInputClick}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      {...props}
    />
  );
}

function AutocompleteEndAdornment({
  children,
}: Readonly<React.ComponentProps<typeof InputGroupAddon>>) {
  return <InputGroupAddon align="inline-end">{children}</InputGroupAddon>;
}

const isAutocompleteOptionDisabled = (option: unknown) =>
  Boolean((option as { disabled?: boolean } | undefined)?.disabled);

function AutocompleteContent<TData>({
  getOptionExtra,
  getOptionHidden,
  groupBy,
  noOptionsText,
  renderGroupHeader,
  itemProps,
  ...props
}: Readonly<
  React.ComponentProps<typeof PopoverContent> & {
    noOptionsText: string;
    getOptionExtra?: (option: TData) => React.ReactNode;
    getOptionHidden?: (option: TData) => boolean;
    groupBy?: (option: TData) => string;
    renderGroupHeader?: (group: string, options: TData[]) => React.ReactNode;
    itemProps?: React.ComponentProps<typeof CommandItem> & DataAttributes;
  }
>) {
  const {
    popoverOpen,
    options,
    selectedIndex,
    isSelectedOption,
    setSelectedIndex,
    getOptionLabel,
    onAutocompleteChange,
    setAutocompleteValue,
    formatAutocompleteValue,
    isLoadingOptions,
    inputRef,
    autoFocus,
  } = useAutocompleteContext<TData>();

  const groupedOptions = useMemo(() => {
    const indexedOptions = options.map((option, index) => ({
      option,
      index,
    }));

    if (!groupBy) {
      return [{ group: '', options: indexedOptions }];
    }

    const optionsByGroup = new Map<
      string,
      Array<{ option: TData; index: number }>
    >();

    indexedOptions.forEach((item) => {
      const group = groupBy(item.option);
      const existingGroup = optionsByGroup.get(group);

      if (existingGroup) {
        existingGroup.push(item);
        return;
      }

      optionsByGroup.set(group, [item]);
    });

    return Array.from(optionsByGroup, ([group, options]) => ({
      group,
      options,
    }));
  }, [groupBy, options]);

  const visibleOptionsCount = options.filter(
    (option) => !getOptionHidden?.(option),
  ).length;

  const renderAutocompleteOption = (option: TData, idx: number) => {
    if (getOptionHidden?.(option)) {
      return null;
    }

    const isDisabled = isAutocompleteOptionDisabled(option);

    return (
      <CommandItem
        key={getOptionLabel(option) + `-${idx}`}
        value={getOptionLabel(option)}
        disabled={isDisabled}
        onSelect={(value) => {
          if (value !== null && !isSelectedOption(option) && !isDisabled) {
            onAutocompleteChange(option);
            setAutocompleteValue(
              option ? formatAutocompleteValue(getOptionLabel(option)) : '',
            );
          }
        }}
        className={cn(
          // Basic row layout
          'cursor-pointer overflow-hidden text-[14px] leading-4',

          // Default background
          'bg-ui-primary text-text-primary',

          // Hover background (mouse)
          'hover:bg-ui-hover',

          // Focus background (keyboard highlight)
          'data-[selected=true]:bg-ui-secondary data-[selected=true]:focus:bg-ui-highlight data-[selected=true]:focus:outline-none',

          // Disabled
          'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-text-disabled',

          // Keyboard selected,
          selectedIndex === idx ? 'bg-ui-secondary' : '',

          // Selected value
          isSelectedOption(option) &&
            'bg-background-tertiary text-tertiary-dark hover:bg-background-tertiary',
        )}
        onMouseEnter={() => setSelectedIndex(idx)} // Highlight item on hover
        data-dd-action-name={`Autocomplete option number "${idx + 1}"`}
        {...itemProps}
      >
        <div className="full-width flex h-10 items-center justify-between gap-2 px-3 py-1.5">
          <TruncateText>{getOptionLabel(option)}</TruncateText>
          {getOptionExtra?.(option)}
        </div>
      </CommandItem>
    );
  };

  return (
    <PopoverContent
      align="start"
      side="bottom"
      data-testid="autocomplete-popover"
      className="z-popover flex flex-col justify-between gap-2"
      onOpenAutoFocus={(e) => {
        // Prevent default autofocus behavior.
        // Needed to avoid losing focus on the input => losing the popover
        if (autoFocus) {
          inputRef.current?.focus();
        }
        e.preventDefault();
      }}
      onCloseAutoFocus={(e) => {
        e.preventDefault();
      }}
      {...props}
    >
      <div className={popoverOpen ? 'block' : 'hidden'}>
        <Command>
          <CommandList className="w-(--radix-popover-trigger-width)">
            {visibleOptionsCount === 0 && !groupBy ? (
              <CommandEmpty>{noOptionsText}</CommandEmpty>
            ) : null}

            {groupedOptions.map(({ group, options: groupOptions }) =>
              groupBy ? (
                <CommandGroup
                  key={group}
                  heading={
                    renderGroupHeader?.(
                      group,
                      groupOptions.map(({ option }) => option),
                    ) ?? group
                  }
                >
                  {groupOptions.map(({ option, index }) =>
                    renderAutocompleteOption(option, index),
                  )}
                </CommandGroup>
              ) : (
                groupOptions.map(({ option, index }) =>
                  renderAutocompleteOption(option, index),
                )
              ),
            )}

            {isLoadingOptions && (
              <ProgressCircular size="small" color="primary" className="p-2" />
            )}
          </CommandList>
        </Command>
      </div>
    </PopoverContent>
  );
}

const isPrintableKey = (e: React.KeyboardEvent<HTMLInputElement>) =>
  e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

export {
  Autocomplete,
  AutocompleteInput,
  AutocompleteTrigger,
  AutocompleteContent,
  AutocompleteStartAdornment,
  AutocompleteEndAdornment,
};
